import { create } from 'zustand';
import type { BulkRateOverride, BulkReRateInputMode } from '@/features/trades/utils/bulk-rerate-calculations';

/**
 * LR-539 bulk re-rate staging state (transient — reset each time the modal
 * opens). Mirrors the MTM store shape: a per-row edit map + an excluded set,
 * plus the batch-wide input mode / bps delta / effective date / reason. The
 * `overrides` map is a plain object keyed by id (not a Set) so a row's edit
 * replaces only its own entry; per-row render isolation is realised in the modal
 * via a memoised `BulkRow` (a keystroke changes one `overrides[id]` reference, so
 * only that row re-renders), not by per-slice store subscriptions.
 */
interface BulkReRateState {
  /** Per-row Set-per-row edits. Plain object for cheap per-key subscription. */
  overrides: Record<number, BulkRateOverride>;
  excludedIds: Set<number>;
  mode: BulkReRateInputMode;
  adjustBps: number | null;
  effectiveDate: string;
  reason: string;

  setField: (id: number, field: keyof BulkRateOverride, value: number | undefined) => void;
  toggleExclude: (id: number) => void;
  setMode: (mode: BulkReRateInputMode) => void;
  setAdjustBps: (value: number | null) => void;
  setEffectiveDate: (value: string) => void;
  setReason: (value: string) => void;
  /** Re-initialise for a fresh batch: seed the effective date + the always-excluded
   *  (ineligible) ids, clear every per-row edit and prior manual exclusion. */
  reset: (init: { effectiveDate: string; excludedIds: number[] }) => void;
}

export const useBulkReRateStore = create<BulkReRateState>((set) => ({
  overrides: {},
  excludedIds: new Set<number>(),
  mode: 'set_per_row',
  adjustBps: null,
  effectiveDate: '',
  reason: '',

  setField: (id, field, value) =>
    set((s) => {
      const row = { ...(s.overrides[id] ?? {}) };
      if (value === undefined) delete row[field];
      else row[field] = value;
      return { overrides: { ...s.overrides, [id]: row } };
    }),

  toggleExclude: (id) =>
    set((s) => {
      const next = new Set(s.excludedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { excludedIds: next };
    }),

  setMode: (mode) => set({ mode }),
  setAdjustBps: (adjustBps) => set({ adjustBps }),
  setEffectiveDate: (effectiveDate) => set({ effectiveDate }),
  setReason: (reason) => set({ reason }),

  reset: ({ effectiveDate, excludedIds }) =>
    set({
      overrides: {},
      excludedIds: new Set(excludedIds),
      mode: 'set_per_row',
      adjustBps: null,
      effectiveDate,
      reason: '',
    }),
}));
